import React from "react";
import {getIdWithToken} from "../../../../utils/storage/Token";
import {useFetchDashHome, DashHomeData} from "../../../../service/Business/DashHomeService";
import FCSpinner from "../../../FCObjets/FCSpinner";
import StatusBusinessDeal from "../../../../utils/Business/StatusBusinessDeal";
import "./ContentHome.scss"
import StatusBilling, {TypeStatusBilling} from "../../../../utils/Business/StatusBilling";
import {BusinessDealInterface} from "../../../../service/Business/Interfaces/BusinessDealInterface";
import {BillingInterface} from "../../../../service/Business/Interfaces/BillingInterface";
import FCAlertMsgErrorAPI from "../../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import FCTag from "../../../FCObjets/FCTag";
import formatEuro from "../../../../utils/formatEuro";
import DeclarationAlerts from "./DeclarationAlerts";

type ContentDefaultProps = {
    onNavigate: (menu: string) => void;
};

const ContentHome: React.FunctionComponent<ContentDefaultProps> = ({onNavigate}) => {
    const {data: dataContentHome, loading, error} = useFetchDashHome(getIdWithToken());
    const dashData = dataContentHome as DashHomeData | null;


    /**
     * Formate la date d'échéance
     */
    const formatEcheance = (dateStr: string): { day: string; month: string } => {
        const date = new Date(dateStr);
        const day = date.getDate().toString();
        const month = date.toLocaleDateString('fr-FR', {month: 'short'}).replace('.', '');
        return {day, month: month.charAt(0).toUpperCase() + month.slice(1)};
    };

    /**
     * Calcule le nombre de jours avant l'échéance
     */
    const getDaysUntil = (dateStr: string): number => {
        const now = new Date();
        const target = new Date(dateStr);
        return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    };

    /**
     * Calcule le montant TTC d'une facture
     */
    const getBillingTotal = (billing: BillingInterface): number => {
        if (!billing.billingItems) return 0;
        return billing.billingItems.reduce((sum, item) => sum + item.totalAmountExclTax, 0);
    };

    /**
     * Calcule le pourcentage d'avancement d'un contrat (basé sur les factures payées)
     */
    const getBusinessProgress = (deal: BusinessDealInterface): number => {
        if (!dashData?.billings) return 0;
        const dealBillings = dashData.billings.filter(b => b.businessDeal?.id === deal.id);
        if (dealBillings.length === 0) return 0;
        const paid = dealBillings.filter(b => b.state === TypeStatusBilling.BILL_PAID).length;
        return Math.round((paid / dealBillings.length) * 100);
    };

    if (loading) {
        return (
            <div className="dashHome">
                <div className="dashHomeLoading">
                    <FCSpinner size="large"/>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashHome">
                <FCAlertMsgErrorAPI error={error}/>
            </div>
        );
    }

    const echeance = dashData?.prochaineEcheance;
    const daysUntil = echeance ? getDaysUntil(echeance.date) : null;
    const isUrgent = daysUntil !== null && daysUntil <= 30;

    return (
        <div className="dashHome">
            {/* Header */}
            <div className="dashHomeHeader">
                <div className="dashHomeHeaderLeft">
                    <h1 className="dashHomeTitle">Tableau de bord</h1>
                    <p className="dashHomeSubtitle">Gérez vos prestations et factures avec élégance.</p>
                </div>
                <div className="dashHomeHeaderRight">
                    <button className="dashBtnNewInvoice" onClick={() => onNavigate("billing")}>
                        <span className="material-symbols-outlined">add</span>
                        Nouvelle Facture
                    </button>
                </div>
            </div>

            {/* Bandeaux de déclarations en attente */}
            <DeclarationAlerts onNavigate={onNavigate}/>

            {/* KPI Cards */}
            <div className="dashKpiGrid">
                {/* Revenu Total */}
                <div className="dashKpiCard dashKpiRevenu">
                    <div className="dashKpiLabel">Revenu Total {new Date().getFullYear()}</div>
                    <div className="dashKpiValue">
                        {dashData ? formatEuro(dashData.revenuTotal, 0) : '---'}
                    </div>
                    {dashData?.revenuEvolution !== null && dashData?.revenuEvolution !== undefined && (
                        <div className={`dashKpiEvolution ${dashData.revenuEvolution >= 0 ? 'positive' : 'negative'}`}>
                            <span className="material-symbols-outlined dashKpiEvolutionIcon">
                                {dashData.revenuEvolution >= 0 ? 'trending_up' : 'trending_down'}
                            </span>
                            <span>
                                {dashData.revenuEvolution >= 0 ? '+' : ''}{dashData.revenuEvolution}%
                                <span className="dashKpiEvolutionLabel"> vs {new Date().getFullYear() - 1}</span>
                            </span>
                        </div>
                    )}
                </div>

                {/* Prochaine Échéance */}
                <div className="dashKpiCard">
                    <div className="dashKpiLabel">Prochaine Échéance</div>
                    {echeance ? (
                        <>
                            <div className="dashKpiEcheance">
                                <span className="dashKpiEcheanceDate">
                                    {formatEcheance(echeance.date).day} {formatEcheance(echeance.date).month}.
                                </span>
                                {isUrgent && <span className="dashKpiUrgent">Urgent</span>}
                            </div>
                            <div className="dashKpiEcheanceDesc">{echeance.description}</div>
                        </>
                    ) : (
                        <div className="dashKpiValue">---</div>
                    )}
                </div>

                {/* Estimation Cotisations */}
                <div className="dashKpiCard">
                    <div className="dashKpiLabel">Estimation Cotisations T{Math.floor(new Date().getMonth() / 3) + 1}</div>
                    <div className="dashKpiValue dashKpiCotisations">
                        {dashData ? formatEuro(dashData.estimationCotisations, 0) : '---'}
                        <span className="dashKpiCotisationsLabel">URSSAF</span>
                    </div>
                    <div className="dashKpiEcheanceDesc">Trimestre courant de cotisation</div>
                </div>
            </div>

            {/* Main content grid */}
            <div className="dashContentGrid">
                {/* Prestations en cours */}
                <div className="dashSection dashSectionPrestations">
                    <div className="dashSectionHeader">
                        <h2 className="dashSectionTitle">Prestations en Cours</h2>
                        <button className="dashSectionLink" onClick={() => onNavigate("businessDeal")}>Voir tout</button>
                    </div>

                    {/* Desktop table */}
                    <div className="dashPrestationsTable">
                        <div className="dashPrestationsHead">
                            <span>Client &amp; Mission</span>
                            <span>Avancement</span>
                            <span>Statut</span>
                            <span>Action</span>
                        </div>
                        {dashData?.businessDeals && dashData.businessDeals.length > 0 ? (
                            dashData.businessDeals.slice(0, 5).map((deal: BusinessDealInterface, index: number) => {
                                const progress = getBusinessProgress(deal);
                                return (
                                    <div className="dashPrestationsRow" key={index}>
                                        <div className="dashPrestationsClient">
                                            <div className="dashPrestationsClientName">{deal.customer.companyName}</div>
                                            <div className="dashPrestationsClientDesc">{deal.name}</div>
                                        </div>
                                        <div className="dashPrestationsProgress">
                                            <div className="dashProgressBar">
                                                <div className="dashProgressFill" style={{width: `${progress}%`}}></div>
                                            </div>
                                            <span className="dashProgressText">{progress}%</span>
                                        </div>
                                        <div className="dashPrestationsStatus">
                                            <FCTag value={StatusBusinessDeal.toString(deal.state)}
                                                   color={StatusBusinessDeal.toColor(deal.state)}
                                                   style={{fontWeight: "bold", fontSize: "11px"}}/>
                                        </div>
                                        <div className="dashPrestationsAction">
                                            <button className="dashBtnIcon" onClick={() => onNavigate("businessDeal")}
                                                    title="Voir le contrat">
                                                <span className="material-symbols-outlined">visibility</span>
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="dashEmptyState">Aucune prestation en cours</div>
                        )}
                    </div>

                    {/* Mobile cards */}
                    <div className="dashPrestationsMobile">
                        {dashData?.businessDeals && dashData.businessDeals.slice(0, 5).map((deal: BusinessDealInterface, index: number) => {
                            const progress = getBusinessProgress(deal);
                            return (
                                <div className="dashPrestationCard" key={index} onClick={() => onNavigate("businessDeal")}>
                                    <div className="dashPrestationCardHeader">
                                        <span className="dashPrestationCardClient">{deal.customer.companyName}</span>
                                        <span className="dashPrestationCardAmount">
                                            {deal.details?.tjm ? `${deal.details.tjm} € HT` : ''}
                                        </span>
                                    </div>
                                    <div className="dashPrestationCardName">{deal.name}</div>
                                    <div className="dashPrestationCardProgress">
                                        <span>Livraison prévue : {deal.details?.end ? new Date(deal.details.end).toLocaleDateString('fr-FR', {day: '2-digit', month: 'short'}) : '---'}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Liste des factures */}
                <div className="dashSection dashSectionFactures">
                    <div className="dashSectionHeader">
                        <h2 className="dashSectionTitle">Liste des factures en cours</h2>
                        <button className="dashSectionLink" onClick={() => onNavigate("billing")}>Voir tout</button>
                    </div>

                    <div className="dashFacturesList">
                        {dashData?.billings && dashData.billings.length > 0 ? (
                            dashData.billings.slice(0, 5).map((billing: BillingInterface, index: number) => {
                                const total = getBillingTotal(billing);
                                if(billing.state === TypeStatusBilling.BILL_PAID || 
                                      billing.state === TypeStatusBilling.CANCELED) return null; // Ne pas afficher les factures déjà payées
                                return (
                                    <div className="dashFactureCard" key={index} onClick={() => onNavigate("billing")}>
                                        <div className="dashFactureCardLeft">
                                            <div className="dashFactureCardTag">
                                                <FCTag value={StatusBilling.toString(billing.state)}
                                                       color={StatusBilling.toColor(billing.state)}
                                                       style={{fontWeight: "bold", fontSize: "10px"}}/>
                                            </div>
                                            <div className="dashFactureCardNumber">FACT #{billing.number}</div>
                                            <div className="dashFactureCardClient">
                                                {billing.businessDeal?.name}
                                            </div>
                                        </div>
                                        <div className="dashFactureCardRight">
                                            <div className="dashFactureCardAmount">{formatEuro(total, 2)}</div>
                                            <div className="dashFactureCardHT">HT</div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="dashEmptyState">Aucune facture en cours</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile: Déclaration CTA */}
            <div className="dashDeclarationCTA">
                <div className="dashDeclarationCTAContent">
                    <h3 className="dashDeclarationCTATitle">Prêt pour votre déclaration ?</h3>
                    {echeance && (
                        <p className="dashDeclarationCTADesc">
                            Le prochain prélèvement URSSAF est dû dans {daysUntil} jours.
                            Nous avons pré-rempli votre déclaration.
                        </p>
                    )}
                    <button className="dashDeclarationCTABtn" onClick={() => onNavigate("resume_dec")}>
                        Vérifier ma déclaration
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ContentHome;
